<script setup>
import {computed, ref} from "vue";
import TonalityIcon from "./TonalityIcon.vue";
import {
  getFrequencyOptions,
  getImportanceOptions,
  getTonalityOptions,
  getWeekdayOptions,
  sendTimeOptions,
  timezoneOptions,
} from "./useTelegramRules.js";
import {t} from "@/controllerLK/GlobusLocalization";
import IconTrash from "@/assets/icons/IconTrash.vue";
import ChevronDownBold from "@/assets/icons/ChevronDownBold.vue";
import ChevronUpBold from "@/assets/icons/ChevronUpBold.vue";

const props = defineProps({
  rule: {
    type: Object,
    required: true,
  },
  availableTopics: {
    type: Array,
    required: true,
  },
  filteredTopics: {
    type: Array,
    required: true,
  },
  topicSearchQuery: {
    type: String,
    default: ""
  }
});

const emit = defineEmits([
  "delete",
  "toggle-topic",
  "toggle-all-topics",
  "toggle-importance",
  "toggle-tonality",
  "set-frequency",
  "set-send-time",
  "set-timezone",
  "set-weekday",
  "update:topic-search-query",
]);

// Состояние открытых дропдаунов
const openDropdowns = ref({});

const toggleDropdown = (dropdownType) => {
  openDropdowns.value[dropdownType] = !openDropdowns.value[dropdownType];
};

const isDropdownOpen = (dropdownType) => {
  return openDropdowns.value[dropdownType] || false;
};

const closeAllDropdowns = () => {
  openDropdowns.value = {};
};

// ### Работа с темами.
// Ref для элемента, который отображает label с выбранными темами.
const topicsLabelRef = ref(null);

// Список наименований всех выбранных тем.
const topicsNames = computed(() => {
  if (props.rule.selectedTopics.length === 0) {
    return [];
  }
  return props.rule.selectedTopics.map(
      (id) => props.availableTopics.find((t) => t.id === id)?.name
  ).filter(Boolean); // фильтруем undefined значения
});

// Функция для подсчёта тем, которые не упомянуты в строке выбранных.
const calculateHiddenTopicsCount = () => {
  if (!topicsLabelRef.value || topicsNames.value.length === 0) {
    return 0;
  }

  const container = topicsLabelRef.value;
  const maxWidth = 357; // Максимальная ширина поля с выбранными темами по Figma.

  // Создаём временный невидимый элемент с теми же стилями
  const tempElement = document.createElement("span");
  tempElement.style.cssText = window.getComputedStyle(container).cssText;
  tempElement.style.position = "absolute";
  tempElement.style.visibility = "hidden";
  tempElement.style.whiteSpace = "nowrap";
  tempElement.style.maxWidth = "none";
  document.body.appendChild(tempElement);

  let visibleCount = 0;
  let currentWidth = 0;
  const separator = ", ";

  // Измеряем ширину "..."
  tempElement.textContent = "...";
  const ellipsisWidth = tempElement.offsetWidth;

  for (let i = 0; i < topicsNames.value.length; i++) {
    const topicText = topicsNames.value[i];
    tempElement.textContent = i === 0
        ? topicText
        : separator + topicText;
    const topicWidth = tempElement.offsetWidth;

    // Проверяем, влезет ли текущая тема + "..." (если это не последняя)
    const willFit = i === topicsNames.value.length - 1
        ? currentWidth + topicWidth <= maxWidth
        : currentWidth + topicWidth + ellipsisWidth <= maxWidth;

    if (willFit) {
      currentWidth += topicWidth;
      visibleCount++;
    } else {
      break;
    }
  }

  document.body.removeChild(tempElement);

  return topicsNames.value.length - visibleCount;
};

// Computed для количества скрытых тем
const hiddenTopicsCount = computed(() => {
  if (topicsNames.value.length === 0) return 0;
  return calculateHiddenTopicsCount();
});

// Label на выбранные темы.
const topicsLabel = computed(() => {
  if (props.rule.selectedTopics.length === 0) {
    return "всех тем проекта";
  }

  if (topicsNames.value.length === 0) {
    return "";
  }

  const visibleCount = topicsNames.value.length - hiddenTopicsCount.value;

  if (visibleCount === topicsNames.value.length) {
    return topicsNames.value.join(", ");
  }

  return topicsNames.value.slice(0, visibleCount).join(", ") + "...";
});

// Получить label частоты
const getFrequencyLabel = (frequency) => {
  return getFrequencyOptions().find((f) => f.value === frequency)?.label || "";
};

// Получить label дня недели
const getWeekdayLabel = (weekday) => {
  return getWeekdayOptions().find((w) => w.value === weekday)?.label || "";
};

// Получить label timezone
const getTimezoneLabel = (timezone) => {
  console.log("get timezone label for", timezone, timezoneOptions);
  const timezoneOption = timezoneOptions.find((item) => item.value === timezone);
  if (timezoneOption) {
    return timezoneOption.label.replace("UTC ", "");
  }
  return "";
};

// Проверить, нужно ли показывать время отправки
const showSendTime = (frequency) => {
  return ["every_3h", "every_6h", "every_24h", "weekly"].includes(frequency);
};

// Получить выбранные цвета значимости в обратном порядке начиная с критичной.
const getSelectedImportanceColors = () => {
  return getImportanceOptions()
      .filter((opt) => props.rule.importance.includes(opt.value))
      .map((opt) => opt.color);
};

// Получить выбранные тональности в обратном порядке начиная с негативной.
const getSelectedTonalities = () => {
  return getTonalityOptions().filter((opt) =>
      props.rule.tonality.includes(opt.value)
  );
};

const handleSetFrequency = (frequency) => {
  emit("set-frequency", props.rule, frequency);
  closeAllDropdowns();
};

const handleSetSendTime = (time) => {
  emit("set-send-time", props.rule, time);
  closeAllDropdowns();
};

const handleSetTimezone = (tz, tz_label) => {
  emit("set-timezone", props.rule, tz, tz_label);
  closeAllDropdowns();
};

const handleSetWeekday = (weekday) => {
  emit("set-weekday", props.rule, weekday);
  closeAllDropdowns();
};

defineExpose({ closeAllDropdowns });

const showChoosedSendTime = (times) => {
  if (!times) return "";
  return times.map((time) => time.split(":").slice(0, 2).join(":")).join("/");
};

const searchBarQuery = computed({
  get() {
    return props.topicSearchQuery;
  },
  set(val) {
    emit("update:topic-search-query", val);
  }
});
</script>

<template>
  <div
    class="rule-card"
    :class="{ 'rule-card--error': rule.errorList.selectedTopicsRequired || rule.errorList.serverError }"
  >
    <!-- Rule Header -->
    <div class="rule-header">
      <div class="rule-for">
        <span class="rule-for__label">{{ t('telegram_subscription.labels.subscription_to') }}</span>

        <!--    Выбор рубрик для правила    -->
        <SimpleDropdown
          v-model:search-bar-query="searchBarQuery"
          width="357px"
          max-width="357px"
          max-height="394px"
          :use-searchbar="true"
          :use-cross-icon="false"
          :is-visible="isDropdownOpen('topics')"
          teleport="#appMain"
          @close="toggleDropdown('topics')"
        >
          <template #trigger>
            <div
              class="rule-for__selector"
              :class="{'rule-for__selector--error': rule.errorList.selectedTopicsRequired}"
              @click="toggleDropdown('topics')"
            >
              <span
                ref="topicsLabelRef"
                class="rule-for__value"
                :class="{
                  'rule-for__value--placeholder':
                    rule.selectedTopics.length === 0 && !rule.isDefault,
                }"
              >
                {{
                  rule.selectedTopics.length === availableTopics.length
                    ? t('telegram_subscription.labels.all_rubrics')
                    : rule.selectedTopics.length === 0
                      ? t('telegram_subscription.labels.none_selected')
                      : topicsLabel
                }}
              </span>
              <span
                v-if="hiddenTopicsCount > 0"
                class="rule-for__badge"
              >
                +{{ hiddenTopicsCount }}
              </span>
              <ChevronUpBold
                v-if="isDropdownOpen('topics')"
                class="rule-for__chevron rule-for__chevron--up"
              />
              <ChevronDownBold
                v-else
                class="rule-for__chevron rule-for__chevron--down"
              />
            </div>
          </template>
          <template #dropdown-item>
            <ListItem
              :item="'all'"
              :model-value="rule.selectedTopics.length === availableTopics.length ? ['all'] : []"
              @click="emit('toggle-all-topics', rule)"
            >
              <Checkbox
                :model-value="rule.selectedTopics.length === availableTopics.length"
              />
              {{ t("telegram_subscription.labels.all") }}
            </ListItem>
            <ListItem
              v-for="topic in filteredTopics"
              :key="topic.id"
              :item="topic.id"
              :model-value="rule.selectedTopics"
              @click="emit('toggle-topic', rule, topic.id)"
            >
              <Checkbox
                :model-value="rule.selectedTopics.includes(topic.id)"
              />
              {{ topic.name }}
            </ListItem>
          </template>
        </SimpleDropdown>
      </div>

      <button
        v-if="!rule.isDefault"
        class="rule-delete"
        @click="emit('delete', rule.id)"
      >
        <IconTrash />
      </button>
    </div>

    <div class="rule-body lk-css-context">
      <!-- Frequency Row - Период получения отчетов: каждые N часов -->
      <SimpleDropdown
        max-height="300px"
        :match-trigger-width="true"
        max-width="100%"
        :use-cross-icon="false"
        trigger-class="dropdown-trigger--flex"
        :is-visible="isDropdownOpen('frequency')"
        style="min-width: 0; overflow: hidden"
        @close="toggleDropdown('frequency')"
      >
        <template #trigger>
          <div class="rule-row">
            <div class="rule-field rule-field--full">
              <span
                class="rule-field__label"
                :class="{'rule-field__label--highlight': isDropdownOpen('frequency')}"
              >{{ t('telegram_subscription.labels.subscription') }}</span>
              <div
                class="rule-field__selector"
                @click="toggleDropdown('frequency')"
              >
                <span class="rule-field__value">{{
                  getFrequencyLabel(rule.frequency)
                }}</span>
                <ChevronUpBold
                  v-if="isDropdownOpen('frequency')"
                  class="rule-field__chevron rule-field__chevron--up"
                />
                <ChevronDownBold
                  v-else
                  class="rule-field__chevron rule-field__chevron--down"
                />
              </div>
            </div>
          </div>
        </template>
        <template #dropdown-item>
          <ListItem
            v-for="option in getFrequencyOptions()"
            :key="option.value"
            :item="option.value"
            :model-value="[rule.frequency]"
            @click="handleSetFrequency(option.value)"
          >
            {{ option.label }}
          </ListItem>
        </template>
      </SimpleDropdown>

      <!-- Send Time Row -->
      <div
        v-if="showSendTime(rule.frequency)"
        class="rule-row"
        :class="rule.frequency === 'weekly' ? 'rule-row--weekly' : 'rule-row--frequency'"
      >
        <!-- Селектор дня недели (только если frequency === weekly) -->
        <SimpleDropdown
          v-if="rule.frequency === 'weekly'"
          max-height="300px"
          max-width="100%"
          :match-trigger-width="true"
          :use-cross-icon="false"
          :is-visible="isDropdownOpen('weekday')"
          teleport="#appMain"
          @close="toggleDropdown('weekday')"
        >
          <template #trigger>
            <div class="rule-field">
              <div
                class="rule-field__selector"
                @click="toggleDropdown('weekday')"
              >
                <span
                  class="rule-field__value"
                  :class="{'rule-field__value--highlight': isDropdownOpen('weekday')}"
                >
                  {{ getWeekdayLabel(rule.weekday) }}
                </span>
                <ChevronUpBold
                  v-if="isDropdownOpen('weekday')"
                  class="rule-field__chevron rule-field__chevron--up"
                />
                <ChevronDownBold
                  v-else
                  class="rule-field__chevron rule-field__chevron--down"
                />
              </div>
            </div>
          </template>
          <template #dropdown-item>
            <ListItem
              v-for="option in getWeekdayOptions()"
              :key="option.value"
              :item="option.value"
              :model-value="[rule.weekday]"
              @click="handleSetWeekday(option.value)"
            >
              {{ option.label }}
            </ListItem>
          </template>
        </SimpleDropdown>

        <!-- Селектор времени рассылки -->
        <SimpleDropdown
          max-height="300px"
          max-width="100%"
          :match-trigger-width="true"
          :use-cross-icon="false"
          :is-visible="isDropdownOpen('sendTime')"
          teleport="#appMain"
          @close="toggleDropdown('sendTime')"
        >
          <template #trigger>
            <div class="rule-field">
              <span
                class="rule-field__label"
                :class="{'rule-field__label--highlight': isDropdownOpen('sendTime')}"
              >
                {{ t('telegram_subscription.labels.time') }}
              </span>
              <div
                class="rule-field__selector"
                @click="toggleDropdown('sendTime')"
              >
                <span class="rule-field__value">{{
                  showChoosedSendTime(rule.sendTime) || ""
                }}</span>
                <ChevronUpBold
                  v-if="isDropdownOpen('sendTime')"
                  class="rule-field__chevron rule-field__chevron--up"
                />
                <ChevronDownBold
                  v-else
                  class="rule-field__chevron rule-field__chevron--down"
                />
              </div>
            </div>
          </template>
          <template #dropdown-item>
            <ListItem
              v-for="option in sendTimeOptions[rule.frequency]"
              :key="option.value"
              :item="option.value"
              :return-object="true"
              :model-value="[rule.sendTime]"
              @click="handleSetSendTime(option.value)"
            >
              {{ option.label }}
            </ListItem>
          </template>
        </SimpleDropdown>
        <!-- Timezone -->
        <SimpleDropdown
          max-height="300px"
          width="120px"
          :match-trigger-width="true"
          :use-cross-icon="false"
          :is-visible="isDropdownOpen('timezone')"
          teleport="#appMain"
          @close="toggleDropdown('timezone')"
        >
          <template #trigger>
            <div class="rule-field">
              <div
                class="rule-field__selector rule-field__selector--right"
                @click="toggleDropdown('timezone')"
              >
                <span class="rule-field__value">
                  <span :class="{'rule-field__value--highlight': isDropdownOpen('timezone')}">UTC</span>
                  {{ getTimezoneLabel(rule.timezone) }}
                </span>
                <ChevronUpBold
                  v-if="isDropdownOpen('timezone')"
                  class="rule-field__chevron rule-field__chevron--up"
                />
                <ChevronDownBold
                  v-else
                  class="rule-field__chevron rule-field__chevron--down "
                />
              </div>
            </div>
          </template>
          <template #dropdown-item>
            <ListItem
              v-for="option in timezoneOptions"
              :key="option.value"
              :item="option.value"
              :model-value="[rule.timezone]"
              @click="handleSetTimezone(option.value, option.label)"
            >
              {{ option.label.replace(" ", "") }}
            </ListItem>
          </template>
        </SimpleDropdown>
      </div>

      <!-- Importance & Tonality Row -->
      <div class="rule-row">
        <!-- Importance -->
        <SimpleDropdown
          max-height="300px"
          max-width="100%"
          :match-trigger-width="true"
          :use-cross-icon="false"
          :is-visible="isDropdownOpen('importance')"
          teleport="#appMain"
          @close="toggleDropdown('importance')"
        >
          <template #trigger>
            <div
              class="rule-field"
              :class="{'rule-field--error-status': rule.errorList.importanceRequired}"
            >
              <span
                class="rule-field__label"
                :class="{'rule-field__label--highlight': isDropdownOpen('importance')}"
              >{{ t('telegram_subscription.labels.importance') }}</span>
              <div
                class="rule-field__selector"
                @click="toggleDropdown('importance')"
              >
                <div class="importance-dots">
                  <span
                    v-for="color in getSelectedImportanceColors()"
                    :key="color"
                    class="importance-dot"
                    :style="{ backgroundColor: color }"
                  />
                </div>
                <ChevronUpBold
                  v-if="isDropdownOpen('importance')"
                  class="rule-field__chevron rule-field__chevron--up"
                />
                <ChevronDownBold
                  v-else
                  class="rule-field__chevron rule-field__chevron--down"
                />
              </div>
            </div>
            <span
              v-if="rule.errorList.importanceRequired && !rule.errorList.importanceTonalityRequired"
              class="rule-field__error-message"
            >
              {{ t("telegram_subscription.errors.importance_required") }}
            </span>
          </template>
          <template #dropdown-item>
            <ListItem
              v-for="option in getImportanceOptions()"
              :key="option.value"
              :item="option.value"
              :model-value="rule.importance"
              @click="emit('toggle-importance', rule, option.value)"
            >
              <Checkbox
                :model-value="rule.importance.includes(option.value)"
              />
              <span
                class="importance-dot importance-dot--label"
                :style="{ backgroundColor: option.color }"
              />
              {{ option.label }}
            </ListItem>
          </template>
        </SimpleDropdown>

        <!-- Tonality -->
        <SimpleDropdown
          max-height="300px"
          max-width="100%"
          :match-trigger-width="true"
          :use-cross-icon="false"
          :is-visible="isDropdownOpen('tonality')"
          teleport="#appMain"
          @close="toggleDropdown('tonality')"
        >
          <template #trigger>
            <div
              class="rule-field"
              :class="{'rule-field--error-status': rule.errorList.tonalityRequired}"
            >
              <span
                class="rule-field__label"
                :class="{'rule-field__label--highlight': isDropdownOpen('tonality')}"
              >{{ t('telegram_subscription.labels.sentiment') }}</span>
              <div
                class="rule-field__selector"
                @click="toggleDropdown('tonality')"
              >
                <div class="tonality-icons">
                  <TonalityIcon
                    v-for="item in getSelectedTonalities()"
                    :key="item.value"
                    :type="item.value"
                    :color="item.color"
                  />
                </div>
                <ChevronUpBold
                  v-if="isDropdownOpen('tonality')"
                  class="rule-field__chevron rule-field__chevron--up"
                />
                <ChevronDownBold
                  v-else
                  class="rule-field__chevron rule-field__chevron--down"
                />
              </div>
            </div>
            <span
              v-if="rule.errorList.tonalityRequired && !rule.errorList.importanceTonalityRequired"
              class="rule-field__error-message"
            >
              {{ t("telegram_subscription.errors.tonality_required") }}
            </span>
          </template>
          <template #dropdown-item>
            <ListItem
              v-for="option in getTonalityOptions()"
              :key="option.value"
              :item="option.value"
              :model-value="rule.tonality"
              @click="emit('toggle-tonality', rule, option.value)"
            >
              <Checkbox
                :model-value="rule.tonality.includes(option.value)"
              />
              <TonalityIcon
                :type="option.value"
                :color="option.color"
              />
              {{ option.label }}
            </ListItem>
          </template>
        </SimpleDropdown>
      </div>
      <span
        v-if="rule.errorList.importanceTonalityRequired"
        class="rule-row__error-message"
      >
        {{ t("telegram_subscription.errors.importance_and_tonality_required") }}
      </span>
    </div>
  </div>
  <!-- Error Message -->
  <div
    v-if="rule.errorList.selectedTopicsRequired || rule.errorList.serverError"
    class="rule-error"
  >
    <template v-if="rule.errorList.selectedTopicsRequired">
      {{ t("telegram_subscription.errors.themes_required") }}
    </template>
    <template v-if="rule.errorList.serverError">
      {{ t("telegram_subscription.errors.server_error") }}
    </template>
  </div>
</template>

<style scoped lang="scss">

.rule-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #F7F8FD;
  border-radius: 8px;
  padding: 8px 14px 14px 14px;

  &--error {
    border: 1px solid var(--system-red);
  }
}

.rule-error {
  color: var(--text-text-error);
  font-size: 12px;
}

.rule-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 30px;
}

.rule-for {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;

  &__label {
    font-weight: 500;
    line-height: 18px;
    color: var(--text-text-main);
    font-size: 14px;
  }

  &__selector {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: var(--text-text-on);
    max-width: 357px;

    &--error {
      color: var(--system-red);
    }
  }

  &__value {
    font-size: 14px;
    font-weight: 500;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__badge {
    background: #1F9AFF;
    color: white;
    font-size: 14px;
    padding: 3px 6px;
    border-radius: 10px;
    font-weight: 500;
  }

  &__chevron {
    min-width: 14px;
    min-height: 14px;
    width: 14px;
    height: 14px;
    pointer-events: none;
  }
}

.rule-delete {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--icon-icon-secondary);

  &:hover {
    color: var(--icon-icon-on);
  }
}

.rule-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rule-row {
  display: flex;
  gap: 10px;

  &__error-message {
    color: var(--system-red);
    font-size: 12px;
  }

  &--frequency {
    display: grid;
    grid-template-columns: 1fr 136px;
  }

  &--weekly {
    display: grid;
    grid-template-columns: 150px 1fr 136px;
  }
}

.rule-field {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #FFF;
  border-radius: 8px;
  padding: 10px 12px;
  min-height: 40px;
  min-width: 0;
  overflow: hidden;
  
  &--error-status {
    border: 1px solid var(--system-red);
  }

  &__error-message {
    display: flex;
    margin-top: 10px;
    color: var(--system-red);
    font-size: 12px;
  }

  &--full {
    width: 100%;
  }

  &__label {
    font-size: 14px;
    white-space: nowrap;
    color: #373D3F;

    &--highlight {
      color: #1F9AFF;
    }
  }

  &__selector {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    flex: 1;
    justify-content: space-between;
    min-width: 0;

    &--right {
      margin-left: 0;
    }
  }

  &__value {
    font-size: 14px;
    color: var(--text-text-pur);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--highlight {
      color: #1F9AFF;
    }
  }

  &__chevron {
    color: $grey;
    min-width: 14px;
    min-height: 14px;
    width: 14px;
    height: 14px;
    pointer-events: none;

    &--up {
      color: #1F9AFF;
    }
  }
}

// Importance dots
.importance-dots {
  display: flex;
  gap: 4px;
}

.importance-dot {
  width: 17px;
  height: 17px;
  border-radius: 50%;
  border: 2px solid transparent;

  &--label {
    width: 14px;
    height: 14px;
    border: none;
  }
}

// Tonality icons
.tonality-icons {
  display: flex;
  gap: 4px;
}

// Для частоты. TODO: Заменить на trigger-class: dropdown-trigger--flex
:deep(.dropdown-trigger) {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

</style>
